/* eslint-disable complexity */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TurnedInNotOutlined } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  TextareaAutosize,
  Typography,
  styled,
  Stack,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { svSE } from '@mui/x-date-pickers/locales';
import { Alert } from 'app/components';
import { setMilestones } from 'app/store/features/chartsSlice';
import { ColorsV2 } from 'app/constants/color';
import { Buttons } from 'app/constants/sizing';
import FileUpload from 'app/pages/insights/compliance/FileUpload';
import apiService from 'app/services/apiService';
import { parseAndFormatDate, showAlert } from 'app/utils';
import dayjs from 'dayjs';
import sv from 'dayjs/locale/sv';

const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '20px',
  marginTop: '20px',
});

const StyledTextarea = styled(TextareaAutosize)(() => ({
  backgroundColor: ColorsV2.primary,
  color: ColorsV2.white,
  borderColor: ColorsV2.outline,
  borderRadius: '8px',
  fontFamily: 'DM Sans, sans-serif',
  fontWeight: '400',
  lineHeight: '20px',
  maxWidth: '97%',
  padding: '10px',
  '::placeholder': {
    color: ColorsV2.textInfo,
    fontFamily: 'DM Sans, sans-serif',
  },
}));

export default function ModelForm({
  setIsMilestoneModelOpen,
  // fetchedMultipleMilstoneData and it's correlated set function is used to keep track of which milestone has been clicked on
  // It is set through the onClick part of the arbitraryLinePlugin function that generates the visual milestones (passed through as a parameter)
  // Setting fetchedMultipleMilestonesdata to an empty array will prompt the user to create a new milestone rather than editing an existing one
  setFetchedMultipleMilestoneData = () => {},
  fetchedMultipleMilestoneData = [],
  multipleMilestoneData = [],
  setMultipleMilestoneData = () => {},
}) {
  const { darkmode } = useSelector(state => state.dark);
  const milestoneData = useSelector(state => state.charts.milestones);

  const dispatch = useDispatch();

  const formattedDate = parseAndFormatDate(fetchedMultipleMilestoneData[0]?.date);

  const newFetchedMilestoneData = (
    multipleMilestoneData?.length ? multipleMilestoneData : fetchedMultipleMilestoneData
  ).map(item => ({
    ...item,
    date: formattedDate,
  }));

  const [data, setData] = useState({
    ...newFetchedMilestoneData[0],
    id: newFetchedMilestoneData[0]?.id,
    date: newFetchedMilestoneData[0]?.date ? newFetchedMilestoneData[0]?.date : '',
    name: newFetchedMilestoneData[0]?.name ? newFetchedMilestoneData[0]?.name : '',
    description: newFetchedMilestoneData[0]?.description
      ? newFetchedMilestoneData[0]?.description
      : '',
    files: newFetchedMilestoneData[0]?.files ? newFetchedMilestoneData[0]?.files : [],
  });
  const [openConfirmation, setOpeConfirmation] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previouslyAddedFiles, setPreviouslyAddedFiles] = useState(
    newFetchedMilestoneData[0]?.files ? newFetchedMilestoneData[0]?.files : []
  );
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleClose = () => {
    setIsMilestoneModelOpen(false);
    setFetchedMultipleMilestoneData([]);
    setMultipleMilestoneData([]);
  };

  const handleChange = e => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const fetchMilestones = async () => {
    try {
        const { milestones } = await apiService.getMilestones();
        dispatch(setMilestones(milestones));
    } catch (error) {
        console.error(error);
    }
  };

  const addNewMilestone = async () => {
    try {
      if (!data.name) throw Error('Name is required');
      if (!data.date) {
        const now = new Date();
        data.date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
      const dateObject = new Date(data.date);
      
      // Adjust for timezone manually
      // Failing to adjust the date/time for timezones can cause dates to be shifted backwards/forwards in 
      const timezoneOffset = dateObject.getTimezoneOffset() * 60000;
      const adjustedDate = new Date(dateObject.getTime() - timezoneOffset);
      
      // Convert to ISO string (expected response in backend)
      const isoString = adjustedDate.toISOString();

      const modifiedData = {
        ...data,
        date: isoString,
        files: await Promise.all(
          selectedFiles.map(
            file =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  resolve({
                    name: '', // Allow user to enter a custom name
                    filename: file.name,
                    fileType: file.type,
                    content: reader.result.split(',')[1],
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
              })
          )
        ),
      };
      const res = await apiService.addMilestones(modifiedData);
      if(res.status === 'success'){
        showAlert('success', t('milestone_created'));
      }
      else {
        showAlert('error', t('milestone_error_create'));
      }

      const { milestones } = await apiService.getMilestones();
      dispatch(setMilestones(milestones));
      setIsMilestoneModelOpen(false);
    } catch (error) {
      showAlert('error', t('milestone_error_create'));
      console.error(error.message);
    }
  };

  const updateMilestone = async () => {
    try {
      const dateObject = new Date(data.date);
      
      // Adjust for timezone manually
      // Failing to adjust the date/time for timezones can cause dates to be shifted backwards/forwards in 
      const timezoneOffset = dateObject.getTimezoneOffset() * 60000;
      const adjustedDate = new Date(dateObject.getTime() - timezoneOffset);
      
      // Convert to ISO string (expected response in backend)
      const isoString = adjustedDate.toISOString();
      const modifiedData = {
        ...data,
        date: isoString,
        deletedFiles,
        files: await Promise.all(
          selectedFiles.map(
            file =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  resolve({
                    name: 'name',
                    filename: file.filename,
                    fileType: file.type,
                    content: reader.result.split(',')[1],
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
              })
          )
        ),
      };
      const res = await apiService.updateMilestone(modifiedData, data.id);
      if(res.data.status === 'success'){
        showAlert('success', t('milestone_updated'));
      } else {
        showAlert('error', t('milestone_error_update'));
      }
    } catch (error) {
      showAlert('error', t('milestone_error_update'));
      console.error(error);
    }
    finally {
      setIsMilestoneModelOpen(false);
      setMultipleMilestoneData([]);
      setFetchedMultipleMilestoneData([]);
      fetchMilestones();
      // Handle delete modal if open
      setDeletedFiles([]);
      setDeleteModalOpen(false);
    }
  };

  const deleteMilestone = async () => {
    setOpeConfirmation(true);
    try {
      await apiService.deleteMilestone(data?.id);
      setIsMilestoneModelOpen(false);
      setFetchedMultipleMilestoneData([]);
      setMultipleMilestoneData([]);
      showAlert('success', t('milestone_deleted'));

      const filtered = milestoneData.filter(item => item.id !== data.id);
      dispatch(setMilestones(filtered));
    } catch (error) {
      showAlert('error', t('milestone_error_delete'));
      console.error(error);
    }
  };

  const StyledCancel = styled(Button)({
    background: '#EFF2F8',
    color: '#4A5F75',
    '&:hover': {
      backgroundColor: ColorsV2.acent,
      color: ColorsV2.white,
    },
  });
  const StyledConfirm = styled(Button)({
    background: ColorsV2.secondary,
    '&:hover': {
      backgroundColor: ColorsV2.acent,
    },
  });
  const CustomAdornment = styled(InputAdornment)({
    fontFamily: 'DM Sans, sans-serif',
    margin: '0px 5px 0px 0px',
    fontWeight: '400',
    lineHeight: '21px',
    color: 'red',
  });
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'identify_headings',
  });
  const today = new Date();

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <Box
          sx={{
            background: ColorsV2.primary,
            width: '600px',
            paddingBottom: '10px',
          }}
        >
          {!fetchedMultipleMilestoneData.length && !multipleMilestoneData.length ? (
            <DialogTitle
              sx={{
                color: ColorsV2.white,
                textAlign: 'center',
              }}
            >
              {t('AddMilestone')}
            </DialogTitle>
          ) : (
            <DialogTitle
              sx={{
                color: ColorsV2.white,
                textAlign: 'center',
              }}
            >
              {t('UpdateMilestone')}
            </DialogTitle>
          )}
          <DialogContent>
            <FormContainer noValidate autoComplete='off'>
              {i18n?.language === 'sv' ? (
                <LocalizationProvider
                  localeText={svSE.components.MuiLocalizationProvider.defaultProps.localeText}
                  adapterLocale={sv}
                  dateAdapter={AdapterDayjs}
                >
                  <DatePicker
                    disableFuture
                    value={data.date ? dayjs(data.date) : dayjs(today)}
                    onChange={newValue => {
                      const formatedDate = parseAndFormatDate(newValue.$d);
                      setData({ ...data, date: formatedDate });
                    }}
                    format='DD MMM YYYY'
                    sx={{
                      border: '1px solid',
                      borderRadius: '5px',
                      overflow: 'hidden',
                      borderColor: ColorsV2.outline,
                      '& svg': { color: ColorsV2.white },
                      '& input': { color: ColorsV2.white },
                      '& .MuiOutlinedInput-input': {
                        paddingY: '10.5px',
                      },
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disableFuture
                    value={data.date ? dayjs(data.date) : dayjs(today)}
                    onChange={newValue => {
                      const formatedDate = parseAndFormatDate(newValue.$d);
                      setData({ ...data, date: formatedDate });
                    }}
                    format='DD MMM YYYY'
                    sx={{
                      border: '1px solid',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      borderColor: ColorsV2.outline,
                      '& svg': {
                        color: ColorsV2.white,
                      },
                      '& input': { color: ColorsV2.white },
                      '& .MuiOutlinedInput-input': {
                        paddingY: '10.5px',
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
              <TextField
                noValidate
                id='name'
                value={data?.name}
                name='name'
                size='small'
                onChange={e => handleChange(e)}
                InputProps={{
                  startAdornment: (
                    <CustomAdornment position='start'>
                      <Typography
                        sx={{
                          color: ColorsV2.textInfo,
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        {t('Name')}:
                      </Typography>
                    </CustomAdornment>
                  ),
                  style: {
                    color: ColorsV2.white,
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: '400',
                    lineHeight: '21px',
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                InputLabelProps={{
                  style: {
                    color: 'inherit',
                  },
                }}
                sx={{
                  width: '100%',
                  backgroundColor: darkmode && 'transparent',

                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: ColorsV2.outline,
                  },
                  '& .MuiOutlinedInput-root': {
                    paddingY: '10.5px',
                    '&:hover fieldset': {
                      borderColor: ColorsV2.outline,
                    },
                  },

                  marginRight: '10px',
                  '& label.Mui-focused': {
                    color: ColorsV2.white,
                    transform: 'translate(15px, -16px)',
                    fontSize: '1rem',
                  },
                  '&  .MuiFormHelperText-root.Mui-error': {
                    background: ColorsV2.primary,
                    color: '#FF2800',
                    width: '101%',
                    height: '100%',
                    fontSize: '1rem',
                    marginTop: '-0.5%',
                    marginLeft: -0.15,
                  },
                }}
              />
              <StyledTextarea
                id='description'
                name='description'
                value={data?.description}
                onChange={e => handleChange(e)}
                placeholder={t('Description')}
                minRows={5}
                InputLabelProps={{
                  style: {
                    color: 'inherit',
                  },
                }}
              />
            </FormContainer>
            <FileUpload
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              previouslyAddedFiles={previouslyAddedFiles}
              setPreviouslyAddedFiles={setPreviouslyAddedFiles}
              setDeletedFiles={setDeletedFiles}
            ></FileUpload>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '0 24px 10px 0',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            {fetchedMultipleMilestoneData.length ? (
              <Button
                name='IconButton'
                variant='text'
                sx={{
                  ...Buttons.baseStyle.primaryButton,
                  marginRight: 'auto',
                  marginLeft: '24px',
                }}
                onClick={e => {
                  e.stopPropagation();
                  setOpeConfirmation(true);
                }}
                startIcon={<DeleteOutlinedIcon />}
              >
                {t('Delete')}
              </Button>
            ) : (
              ''
            )}
            <StyledCancel variant='contained' onClick={handleClose}>
              {t('Cancel')}
            </StyledCancel>
            {fetchedMultipleMilestoneData.length ? (
              // <StyledButton
              //   name={'IconButton'}
              //   sx={{ background: ColorsV2.secondary, fontWeight: 'light' }}
              //   startIcon={<TurnedInNotOutlined fontSize='0.7em' />}
              //   onClick={updateMilestone}
              // >
              //   {t('Update')}
              // </StyledButton>
              <StyledConfirm
                // name={'IconButton'}
                variant='contained'
                color='primary'
                startIcon={<TurnedInNotOutlined fontSize='0.7em' />}
                onClick={deletedFiles?.length > 0 ? () => {setDeleteModalOpen(true)} : updateMilestone}
                // disabled={selectedFiles.length === 0}
              >
                {t('Update')}
              </StyledConfirm>
            ) : (
              <StyledConfirm
                variant='contained'
                color='secondary'
                onClick={addNewMilestone}
                startIcon={<TurnedInNotOutlined fontSize='0.7em' />}
                sx={{ width: i18n.language === 'sv' ? '25%' : '18%' }}
              >
                {t('Add')}
              </StyledConfirm>
            )}
          </DialogActions>
        </Box>
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
            <Stack direction='column' alignItems='center' sx={{
                background: ColorsV2.secondary,
                padding: '20px',
                maxWidth: '400px',
                color: ColorsV2.white,
            }}>
              <Typography variant='body1'>
                {t('DeleteMilestoneText')}
              </Typography>
              <Stack direction='row' justifyContent='flex-end' gap={1} sx={{
                width: '100%',
                marginTop: '20px',
                marginBottom: '20px',
              }}>
                <Button onClick={() => setDeleteModalOpen(false)} sx={{
                  ...Buttons.baseStyle.primaryButtonHighPriority
                }}>
                  {t('Cancel')}
                </Button>
                <Button onClick={() => {updateMilestone(); setDeleteModalOpen(true)}} 
                startIcon={<DeleteOutlinedIcon />}
                sx={{
                  ...Buttons.baseStyle.primaryButtonLowPriority
                }}>
                  
                  {t('Update')}
                </Button>
              </Stack>
            </Stack>
        </Dialog>
      </Dialog>
      <Alert
        open={openConfirmation}
        title={t('DeleteMilestone')}
        text={t('DeleteMilestoneText')}
        setOpen={setOpeConfirmation}
        handleConfirmation={deleteMilestone}
      />
    </>
  );
}
